import { PLAN_LIMITS, PRICING_RATES, SUPPORT_LIMITS } from './pricingData.js';

export const CALCULATOR_PLAN_IDS = ['live', 'starter', 'standard', 'pro', 'elite'];
export const CALCULATOR_PLANS = CALCULATOR_PLAN_IDS.map(id => PLAN_LIMITS.find(plan => plan.id === id));
export const CALCULATOR_SUPPORT = SUPPORT_LIMITS;
const MAX_COUNT = 1_000_000_000;
const supportIndex = id => Math.max(0, SUPPORT_LIMITS.findIndex(support => support.id === id));
const count = value => Number.isFinite(Number(value)) ? Math.min(MAX_COUNT, Math.max(0, Math.floor(Number(value)))) : 0;
const cents = amount => Math.round(amount * 100);
const includesWhatsApp = id => id === 'pro' || id === 'elite';
const getRates = locale => PRICING_RATES[locale === 'tr' ? 'tr' : 'en'];

export function getCalculatorPlan(id) {
  return CALCULATOR_PLANS.find(plan => plan.id === id) || CALCULATOR_PLANS.find(plan => plan.id === 'standard');
}

export function defaultConfiguration(planId = 'standard') {
  const plan = getCalculatorPlan(planId);
  return {
    planId: plan.id, volume: plan.messages, seats: plan.seats, channels: plan.channels,
    automations: plan.automations || 0, supportId: plan.support, whatsapp: includesWhatsApp(plan.id),
  };
}

export function normalizeConfiguration(input = {}) {
  const plan = getCalculatorPlan(input.planId);
  const minimumSupport = supportIndex(plan.support);
  return {
    planId: plan.id,
    volume: plan.id === 'live' ? 0 : count(input.volume ?? plan.messages),
    seats: Math.max(plan.seats, count(input.seats ?? plan.seats)),
    channels: Math.max(plan.channels, count(input.channels ?? plan.channels)),
    automations: Math.max(plan.automations || 0, count(input.automations ?? plan.automations)),
    supportId: SUPPORT_LIMITS[Math.max(minimumSupport, supportIndex(input.supportId))].id,
    whatsapp: includesWhatsApp(plan.id) || input.whatsapp === true,
  };
}

// A per-100 price in currency units equals the per-message price in cents.
// Integer cents keep fractional USD message charges stable at tier boundaries.
function tieredCents(volume, rates) {
  const n = count(volume);
  return Math.round(
    Math.min(n, 10000) * rates.messages[0]
    + Math.max(0, Math.min(n, 50000) - 10000) * rates.messages[1]
    + Math.max(0, n - 50000) * rates.messages[2]
  );
}

export function tieredCost(volume, locale = 'tr') {
  return tieredCents(volume, getRates(locale)) / 100;
}

function costsForPlan(plan, configuration, rates) {
  const extra = {
    seats: Math.max(0, configuration.seats - plan.seats),
    channels: Math.max(0, configuration.channels - plan.channels),
    automations: Math.max(0, configuration.automations - (plan.automations || 0)),
  };
  const amounts = {
    base: cents(rates.plans[plan.id]),
    // The allowance offsets the cost of total volume, not the first overage tier.
    messages: plan.id === 'live' ? 0 : Math.max(0, tieredCents(configuration.volume, rates) - tieredCents(plan.messages, rates)),
    seats: extra.seats * cents(rates.seat),
    channels: extra.channels * cents(rates.channel),
    automations: extra.automations * cents(rates.automation),
    whatsapp: configuration.whatsapp && !includesWhatsApp(plan.id) ? cents(rates.whatsapp) : 0,
    support: Math.max(0, cents(rates.support[supportIndex(configuration.supportId)]) - cents(rates.support[supportIndex(plan.support)])),
  };
  return { extra, amounts, totalCents: Object.values(amounts).reduce((total, value) => total + value, 0) };
}

export function calculatePrice(input, locale = 'tr') {
  const configuration = normalizeConfiguration(input);
  const plan = getCalculatorPlan(configuration.planId);
  const rates = getRates(locale);
  const result = costsForPlan(plan, configuration, rates);
  const setup = plan.id === 'live' ? 0 : rates.setup;
  const comparisons = plan.id === 'live' ? [] : CALCULATOR_PLANS
    .filter(other => other.id !== 'live' && CALCULATOR_PLAN_IDS.indexOf(other.id) < CALCULATOR_PLAN_IDS.indexOf(plan.id))
    .map(other => {
      const equivalent = costsForPlan(other, configuration, rates);
      return {
        planId: other.id, total: equivalent.totalCents / 100,
        savings: (equivalent.totalCents - result.totalCents) / 100,
        savingsPercent: (equivalent.totalCents - result.totalCents) / equivalent.totalCents * 100,
      };
    }).filter(comparison => comparison.savings > 0);
  return {
    configuration, extra: result.extra,
    amounts: Object.fromEntries(Object.entries(result.amounts).map(([key, value]) => [key, value / 100])),
    monthly: result.totalCents / 100, setup,
    firstMonth: (result.totalCents + cents(setup)) / 100,
    includedMessages: plan.messages, extraMessages: Math.max(0, configuration.volume - plan.messages),
    whatsappIncluded: includesWhatsApp(plan.id), comparisons,
  };
}
